import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, Package, BookOpen, ShoppingCart, Settings, 
  Users, Image, Palette, Leaf, ArrowLeft, UserCog, Frame, Megaphone, Gift, MessageSquareWarning,
  DollarSign, Wallet, Truck
} from 'lucide-react';

// Import admin components
import {
  AdminDashboard,
  AdminProducts,
  AdminRecipes,
  AdminOrders,
  AdminShowcases,
  AdminArtworks,
  AdminCommunity,
  AdminFarms,
  AdminSettings,
  AdminUsers,
  AdminExhibitions,
  AdminPosters,
  AdminLanguageSelector,
  AdminCustomizeOrders,
} from '@/components/admin';
import AdminTranslationFeedback from '@/components/admin/AdminTranslationFeedback';
import AdminExchangeRates from '@/components/admin/AdminExchangeRates';
import AdminWithdrawals from '@/components/admin/AdminWithdrawals';
import AdminShipments from '@/components/admin/AdminShipments';
import AdminArtworkPurchases from '@/components/admin/AdminArtworkPurchases';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, isAdmin, loading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [adminLang, setAdminLang] = useState('zh');  // 当前编辑的语言版本
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showcases, setShowcases] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [aboutUs, setAboutUs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
    fetchCategories();
  }, [isAdmin, authLoading, token]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setProductCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const timeout = 15000; // 15秒超时，防止弱网环境下永远加载
      const cfg = { headers, timeout };
      const safe = (promise) => promise.catch(() => ({ data: [] }));
      const [productsRes, recipesRes, ordersRes, statsRes, aboutRes, showcasesRes, artworksRes, postsRes, usersRes, farmsRes] = await Promise.all([
        safe(axios.get(`${API}/products?active_only=false`, cfg)),
        safe(axios.get(`${API}/recipes?active_only=false`, cfg)),
        safe(axios.get(`${API}/admin/orders`, cfg)),
        safe(axios.get(`${API}/admin/stats`, cfg)),
        axios.get(`${API}/settings/about`, cfg).catch(() => ({ data: {} })),
        safe(axios.get(`${API}/showcases?active_only=false`, cfg)),
        safe(axios.get(`${API}/artworks?active_only=false`, cfg)),
        safe(axios.get(`${API}/posts`, cfg)),
        safe(axios.get(`${API}/admin/users`, cfg)),
        safe(axios.get(`${API}/farms?active_only=false`, cfg)),
      ]);
      setProducts(productsRes.data);
      setRecipes(recipesRes.data);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
      setAboutUs(aboutRes.data);
      setShowcases(showcasesRes.data);
      setArtworks(artworksRes.data);
      setPosts(postsRes.data);
      setUsers(usersRes.data);
      setFarms(farmsRes.data);
    } catch (err) {
      console.error('Failed to fetch admin data', err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-gray-500">加载中... / Loading...</div>;
  if (!isAdmin) return null;
  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-gray-500">
      <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-green-600 rounded-full"></div>
      <span>加载中... / Loading...</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white h-screen sticky top-0 border-r flex flex-col">
          <div className="p-6 border-b flex-shrink-0">
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="w-8 h-8 text-green-600" />
              <span className="font-bold text-green-800">Admin Panel</span>
            </div>
            {/* 语言选择器 */}
            <AdminLanguageSelector 
              currentLang={adminLang} 
              onLangChange={setAdminLang}
              allowedLanguages={user?.admin_languages?.length > 0 ? user.admin_languages : []}
            />
          </div>
          <nav className="p-4 space-y-1 flex-1 overflow-y-auto pb-20">
            <Button variant={tab === 'dashboard' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('dashboard')} data-testid="admin-dashboard-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <Button variant={tab === 'showcases' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('showcases')} data-testid="admin-showcases-tab">
              <Image className="w-4 h-4 mr-2" /> Showcases
            </Button>
            <Button variant={tab === 'artworks' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('artworks')} data-testid="admin-artworks-tab">
              <Palette className="w-4 h-4 mr-2" /> Artworks
            </Button>
            <Button variant={tab === 'exhibitions' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('exhibitions')} data-testid="admin-exhibitions-tab">
              <Frame className="w-4 h-4 mr-2" /> Exhibitions
            </Button>
            <Button variant={tab === 'posters' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('posters')} data-testid="admin-posters-tab">
              <Megaphone className="w-4 h-4 mr-2" /> Posters
            </Button>
            <Button variant={tab === 'products' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('products')} data-testid="admin-products-tab">
              <Package className="w-4 h-4 mr-2" /> Products
            </Button>
            <Button variant={tab === 'recipes' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('recipes')} data-testid="admin-recipes-tab">
              <BookOpen className="w-4 h-4 mr-2" /> Recipes
            </Button>
            <Button variant={tab === 'orders' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('orders')} data-testid="admin-orders-tab">
              <ShoppingCart className="w-4 h-4 mr-2" /> Orders
            </Button>
            <Button variant={tab === 'customizeOrders' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('customizeOrders')} data-testid="admin-customize-orders-tab">
              <Gift className="w-4 h-4 mr-2" /> Custom Orders
            </Button>
            <Button variant={tab === 'artworkPurchases' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('artworkPurchases')} data-testid="admin-artwork-purchases-tab">
              <DollarSign className="w-4 h-4 mr-2" /> Artwork Purchases
            </Button>
            <Button variant={tab === 'exchangeRates' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('exchangeRates')} data-testid="admin-exchange-rates-tab">
              <DollarSign className="w-4 h-4 mr-2" /> Exchange Rates
            </Button>
            <Button variant={tab === 'withdrawals' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('withdrawals')} data-testid="admin-withdrawals-tab">
              <Wallet className="w-4 h-4 mr-2" /> Withdrawals
            </Button>
            <Button variant={tab === 'shipments' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('shipments')} data-testid="admin-shipments-tab">
              <Truck className="w-4 h-4 mr-2" /> Shipments
            </Button>
            <Button variant={tab === 'community' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('community')} data-testid="admin-community-tab">
              <Users className="w-4 h-4 mr-2" /> Community
            </Button>
            <Button variant={tab === 'users' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('users')} data-testid="admin-users-tab">
              <UserCog className="w-4 h-4 mr-2" /> Users
            </Button>
            <Button variant={tab === 'farms' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('farms')} data-testid="admin-farms-tab">
              <Leaf className="w-4 h-4 mr-2" /> Farms
            </Button>
            <Button variant={tab === 'translationFeedback' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('translationFeedback')} data-testid="admin-translation-feedback-tab">
              <MessageSquareWarning className="w-4 h-4 mr-2" /> Translation Feedback
            </Button>
            <Button variant={tab === 'settings' ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => setTab('settings')} data-testid="admin-settings-tab">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
          </nav>
          <div className="p-4 border-t bg-white flex-shrink-0">
            <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Site
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {tab === 'dashboard' && <AdminDashboard stats={stats} />}
          {tab === 'showcases' && <AdminShowcases showcases={showcases} onRefresh={fetchData} />}
          {tab === 'artworks' && <AdminArtworks artworks={artworks} products={products} onRefresh={fetchData} />}
          {tab === 'exhibitions' && <AdminExhibitions />}
          {tab === 'posters' && <AdminPosters token={token} />}
          {tab === 'products' && <AdminProducts products={products} onRefresh={fetchData} productCategories={productCategories} onCategoryRefresh={fetchCategories} />}
          {tab === 'recipes' && <AdminRecipes recipes={recipes} onRefresh={fetchData} />}
          {tab === 'orders' && <AdminOrders orders={orders} onRefresh={fetchData} />}
          {tab === 'customizeOrders' && <AdminCustomizeOrders />}
          {tab === 'artworkPurchases' && <AdminArtworkPurchases token={token} />}
          {tab === 'exchangeRates' && <AdminExchangeRates token={token} />}
          {tab === 'withdrawals' && <AdminWithdrawals token={token} />}
          {tab === 'shipments' && <AdminShipments token={token} />}
          {tab === 'community' && <AdminCommunity posts={posts} users={users} onRefresh={fetchData} />}
          {tab === 'users' && <AdminUsers users={users} onRefresh={fetchData} />}
          {tab === 'farms' && <AdminFarms farms={farms} onRefresh={fetchData} />}
          {tab === 'translationFeedback' && <AdminTranslationFeedback token={token} />}
          {tab === 'settings' && <AdminSettings aboutUs={aboutUs} onRefresh={fetchData} />}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
